import { css } from '@emotion/react';
import { t } from 'i18n';
import { useState } from 'react';
import { formatLongRules, type BoundFlight, type Cabin, type FareRules } from 'shared';

import { BoundFlightComponent } from '../BoundFlightComponent';
import { CabinClassAndShortRules } from '../CabinClassAndShortRules';
import { SasIcon, SasIconButton } from '../SasIcon';
import { SasModal } from '../SasModal';
import { theme } from '../theme';

const styles = {
  divider: css`
    background-color: ${theme.colors.separator};
    width: 100%;
    margin-block: 0;
  `,
  title: css`
    margin-top: 0;
  `,
  boundContainer: css`
    border: 1px solid ${theme.colors.border};
    border-radius: ${theme.borders.radius.md};
    margin-right: ${theme.spacing.md};
    background-color: ${theme.colors.white};
    height: fit-content;
    @media (max-width: ${theme.breakpoints.laptop}) {
      margin: ${theme.spacing.md} 0 ${theme.spacing.md} 0;
      :first-of-type {
        margin-top: 0;
      }
    }
  `,
  flightHeadingText: css`
    margin: 0 0 ${theme.spacing.sm} ${theme.spacing.sm};
    color: ${theme.colors.blue[2]};
  `,
  boundWrapper: css`
    padding: ${theme.spacing.md};
  `,
  boundHeadingWrapper: css`
    display: flex;
    justify-content: space-between;
  `,
  boundHeadingContainer: css`
    display: flex;
  `,
};

type Props = {
  headingText: string;
  iconName: string;
  bound: BoundFlight | BoundFlight;
  cabin: Cabin;
  stops: number;
  fareRules: FareRules | undefined;
  includedShortRules?: string[];
};
export const CartBound = ({ headingText, bound, iconName, cabin, stops, fareRules, includedShortRules }: Props) => {
  const [showProductRule, setShowProductRule] = useState(false);

  return (
    <div css={styles.boundContainer}>
      <div css={styles.boundWrapper}>
        <div css={styles.boundHeadingWrapper}>
          <div css={styles.boundHeadingContainer}>
            <SasIcon name={iconName} style={{ marginBottom: '8px' }} />
            <h4 css={styles.flightHeadingText}>{headingText}</h4>
            <SasModal
              open={showProductRule}
              handleClose={e => {
                e.preventDefault();
                setShowProductRule(false);
              }}
            >
              <>
                <h3 css={styles.title}>{cabin.productName}</h3>
                {fareRules && (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: formatLongRules(fareRules.longRules),
                    }}
                  />
                )}
                <p>
                  <strong>{t('class', { class: Array.from(cabin.fareKey).join(', ') })}</strong>
                </p>
              </>
            </SasModal>
          </div>
          {(fareRules?.longRules ?? []).length > 0 && (
            <label tabIndex={1} onKeyDown={e => e.key === 'Enter' && setShowProductRule(true)}>
              <SasIconButton
                name="cl--info-circle"
                color={theme.colors.blue[2]}
                clickable={true}
                onClick={() => {
                  setShowProductRule(true);
                }}
              />
            </label>
          )}
        </div>
        <hr css={styles.divider} />
        <BoundFlightComponent bound={bound} stops={stops} />
        <hr css={styles.divider} />
        {fareRules && (
          <CabinClassAndShortRules
            includedShortRules={includedShortRules}
            displayIcons
            cabinClassName={cabin.productName}
            shortRules={fareRules.shortRules}
          />
        )}
      </div>
    </div>
  );
};
